import { ReactNode } from "react";

import { DayHeaderContentArg, EventInput } from "@fullcalendar/core/index.js";
import { Stack } from "@mui/material";

import { CalendarBase } from "./CalendarBase";
import { WeekDayHeaderTitle } from "./WeekDayHeaderTitle";
import { WeekCalendarHeader } from "./components/calendar-header/WeekCalendarHeader";
import { useCalendar } from "./hooks/useCalendar";

interface WeekCalendarProps<T> {
  events: T[];
  backgroundEvents?: EventInput[];
  noEventsMessage: string;
  renderEvent: (event: T) => ReactNode;
  onDateChange: (startDate: Date, endDate: Date) => void;
  onEventClick: (event: T) => void;
  customHeaderFilter?: ReactNode;
  isLoading: boolean;
  mapEvent: (event: T) => EventInput;
  dayHeaderContent?: (arg: DayHeaderContentArg) => ReactNode;
}

export function WeekCalendar<T>(props: WeekCalendarProps<T>) {
  const {
    events,
    backgroundEvents,
    noEventsMessage,
    renderEvent,
    onDateChange,
    onEventClick,
    customHeaderFilter,
    isLoading,
    mapEvent,
    dayHeaderContent,
  } = props;
  const { calendarRef, goNext, goPrev, goToday, calendarEvents, handleEventContent, handleEventClick } = useCalendar({
    events,
    renderEvent,
    onEventClick,
    mapEvent,
  });

  const defaultDayHeaderContent = (arg: DayHeaderContentArg) => <WeekDayHeaderTitle arg={arg} />;

  return (
    <Stack height="100%" flex={1} borderRadius={0.5} border={"1px solid"} borderColor={"fsw.blue-10"}>
      <WeekCalendarHeader
        onPrevClick={goPrev}
        onNextClick={goNext}
        onTodayClick={goToday}
        customHeaderFilter={customHeaderFilter}
      />

      <CalendarBase
        calendarRef={calendarRef}
        calendarEvents={calendarEvents}
        backgroundEvents={backgroundEvents}
        handleEventContent={handleEventContent}
        handleEventClick={handleEventClick}
        noEventsMessage={noEventsMessage}
        onDateChange={onDateChange}
        isLoading={isLoading}
        initialView="timeGridWeek"
        dayHeaderContent={dayHeaderContent ?? defaultDayHeaderContent}
      />
    </Stack>
  );
}
